import React from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import './MappingFlow.css'

export type MappingStep = 1 | 2 | 3 | 4

type MappingFlowStepperProps = {
  currentStep: MappingStep
  /** Source / mapped-from norm (e.g. NIST) — enables step 2 link */
  sourceNorm?: string
  /** Family id — enables step 3 link */
  familyId?: string
  /** Control id — enables step 4 link */
  controlId?: string
}

const STEP_KEYS = [
  'selectTarget',
  'chooseFamily',
  'chooseControl',
  'reviewMapping',
] as const

/**
 * Horizontal stepper for the 4-step framework mapping flow.
 * Completed steps are clickable when route params are available.
 */
export default function MappingFlowStepper({
  currentStep,
  sourceNorm,
  familyId,
  controlId,
}: MappingFlowStepperProps) {
  const { t } = useTranslation()

  const hrefFor = (step: MappingStep): string | null => {
    if (step === 1) return '/securityFramework'
    if (step === 2 && sourceNorm) return `/norms/${sourceNorm}`
    if (step === 3 && sourceNorm && familyId)
      return `/securityControlsToMap/${sourceNorm}/${familyId}`
    if (step === 4 && sourceNorm && controlId)
      return `/relatedDataMapped/${sourceNorm}/${controlId}`
    return null
  }

  return (
    <nav
      className="mapping-stepper"
      data-tour-id="mapping-stepper"
      aria-label={t('frameworkMappingPage.stepperLabel', {
        defaultValue: 'Framework mapping steps',
      })}
    >
      <ol className="mapping-stepper-list">
        {STEP_KEYS.map((key, index) => {
          const step = (index + 1) as MappingStep
          const done = step < currentStep
          const active = step === currentStep
          const href = done || active ? hrefFor(step) : null
          const label = t(`frameworkMappingPage.steps.${key}`)
          const statusClass = active
            ? 'is-active'
            : done
              ? 'is-done'
              : 'is-todo'

          const inner = (
            <>
              <span className="mapping-stepper-index" aria-hidden>
                {done ? '✓' : String(step).padStart(2, '0')}
              </span>
              <span className="mapping-stepper-copy">
                <span className="mapping-stepper-meta">
                  {t('frameworkMappingPage.stepShort', {
                    step,
                    total: 4,
                    defaultValue: `Step ${step} of 4`,
                  })}
                </span>
                <span className="mapping-stepper-title">{label}</span>
              </span>
            </>
          )

          return (
            <li
              key={key}
              className={`mapping-stepper-item ${statusClass}`}
              aria-current={active ? 'step' : undefined}
            >
              {href && !active ? (
                <NavLink to={href} className="mapping-stepper-link">
                  {inner}
                </NavLink>
              ) : (
                <div className="mapping-stepper-link is-static">{inner}</div>
              )}
              {step < 4 ? (
                <span className="mapping-stepper-connector" aria-hidden />
              ) : null}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
